import React, { useCallback, useState, useRef } from 'react';
import { HStack, Stack, useToast, IconButton, Tooltip } from '@chakra-ui/react';
import { FcRedo, FcUndo } from 'react-icons/fc';
import { FiSave, FiPlus, FiAlignCenter } from 'react-icons/fi';
import {
  Controls,
  MiniMap,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  getConnectedEdges,
  getIncomers,
  getOutgoers,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';

import useUserStore from '../../Hooks/Zustand/Store';
import useFlowchartStore from '../../Hooks/Zustand/flowchartStore';
import UseUndoRedo from '../../Components/Flowchart/useUndoRedo';
import { addDocumentFirebase } from '../../Api/firebaseApi';
import nodeTypes from './Mindmap/NodeType';
import edgeTypes from './Mindmap/EdgeType';

// ==================== CONSTANTS ====================
const PRO_OPTIONS = { account: 'paid-pro', hideAttribution: true };
const FIT_VIEW_OPTIONS = { padding: 0.2, duration: 300 };
const NODE_WIDTH = 150;
const NODE_HEIGHT = 50;

// ==================== AUTO LAYOUT FUNCTION ====================
/**
 * Auto-arrange nodes using Dagre algorithm
 */
const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

// ==================== MAIN COMPONENT ====================
function Mindmap() {
  const toast = useToast();
  const globalState = useUserStore();
  const reactFlowInstance = useReactFlow();
  const [selectedNode, setSelectedNode] = useState(null);
  const nodeIdCounter = useRef(1);

  // Store selectors
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    setNodes,
    setEdges,
  } = useFlowchartStore((state) => ({
    nodes: state.nodes,
    edges: state.edges,
    onNodesChange: state.onNodesChange,
    onEdgesChange: state.onEdgesChange,
    onConnect: state.onConnect,
    setNodes: state.setNodes,
    setEdges: state.setEdges,
  }));

  // Undo/Redo hooks
  const { undo, redo, canUndo, canRedo, takeSnapshot } = UseUndoRedo();

  // ==================== KEYBOARD SHORTCUTS ====================
  
  /**
   * Handle keyboard shortcuts
   */
  const onKeyDown = useCallback(
    (event) => {
      // Enter - Add child node
      if (event.key === 'Enter' && selectedNode) {
        event.preventDefault();
        addChildNode(selectedNode);
      }
      
      // Tab - Add sibling node
      if (event.key === 'Tab' && selectedNode) {
        event.preventDefault();
        addSiblingNode(selectedNode);
      }
      
      // Delete - handled by ReactFlow
      // Ctrl+Z - Undo (handled by useUndoRedo)
      // Ctrl+Y - Redo (handled by useUndoRedo)
    },
    [selectedNode, nodes, edges]
  );

  // ==================== NODE OPERATIONS ====================
  
  /**
   * Add a child node to selected node
   */
  const addChildNode = useCallback(
    (parentNode) => {
      if (!parentNode) return;

      takeSnapshot();

      const newNodeId = `node-${Date.now()}`;
      const newNode = {
        id: newNodeId,
        type: 'custom',
        position: {
          x: parentNode.position.x,
          y: parentNode.position.y + 150,
        },
        data: { label: 'New Node' },
      };

      const newEdge = {
        id: `edge-${parentNode.id}-${newNodeId}`,
        source: parentNode.id,
        target: newNodeId,
        type: 'floating',
      };

      setNodes([...nodes, newNode]);
      setEdges([...edges, newEdge]);

      toast({
        title: 'Node added',
        description: 'Press Enter to add more',
        status: 'success',
        duration: 1500,
      });
    },
    [nodes, edges, setNodes, setEdges, takeSnapshot, toast]
  );

  /**
   * Add a sibling node (same level)
   */
  const addSiblingNode = useCallback(
    (selectedNode) => {
      if (!selectedNode) return;

      takeSnapshot();

      // Find parent of selected node
      const parentEdge = edges.find((edge) => edge.target === selectedNode.id);
      
      const newNodeId = `node-${Date.now()}`;
      const newNode = {
        id: newNodeId,
        type: 'custom',
        position: {
          x: selectedNode.position.x + 200,
          y: selectedNode.position.y,
        },
        data: { label: 'New Node' },
      };

      let newEdges = [...edges];
      
      if (parentEdge) {
        // If has parent, connect to same parent
        const newEdge = {
          id: `edge-${parentEdge.source}-${newNodeId}`,
          source: parentEdge.source,
          target: newNodeId,
          type: 'floating',
        };
        newEdges = [...edges, newEdge];
      }

      setNodes([...nodes, newNode]);
      setEdges(newEdges);

      toast({
        title: 'Sibling node added',
        description: 'Press Tab to add more siblings',
        status: 'success',
        duration: 1500,
      });
    },
    [nodes, edges, setNodes, setEdges, takeSnapshot, toast]
  );

  /**
   * Quick add node button (floating)
   */
  const quickAddNode = useCallback(() => {
    takeSnapshot();

    const viewport = reactFlowInstance.getViewport();
    const newNodeId = `node-${Date.now()}`;
    
    const newNode = {
      id: newNodeId,
      type: 'custom',
      position: {
        x: (window.innerWidth / 2 - viewport.x) / viewport.zoom,
        y: (window.innerHeight / 2 - viewport.y) / viewport.zoom,
      },
      data: { label: 'New Node' },
    };

    setNodes([...nodes, newNode]);

    toast({
      title: 'Node created',
      description: 'Select and press Enter to add children',
      status: 'success',
      duration: 2000,
    });
  }, [nodes, setNodes, reactFlowInstance, takeSnapshot, toast]);

  // ==================== AUTO LAYOUT ====================
  
  /**
   * Auto-arrange nodes in hierarchical layout
   */
  const autoLayout = useCallback(() => {
    takeSnapshot();
    
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      nodes,
      edges,
      'TB' // Top to Bottom
    );

    setNodes(layoutedNodes);
    setEdges(layoutedEdges);

    // Fit view after layout
    setTimeout(() => {
      reactFlowInstance.fitView(FIT_VIEW_OPTIONS);
    }, 50);

    toast({
      title: 'Layout applied',
      description: 'Nodes auto-arranged',
      status: 'success',
      duration: 2000,
    });
  }, [nodes, edges, setNodes, setEdges, reactFlowInstance, takeSnapshot, toast]);

  // ==================== EVENT HANDLERS ====================
  
  /**
   * Handle node selection
   */
  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  /**
   * Handle pane click (deselect)
   */
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  /**
   * Handle node drag start
   */
  const onNodeDragStart = useCallback(() => {
    takeSnapshot();
  }, [takeSnapshot]);

  /**
   * Handle selection drag start
   */
  const onSelectionDragStart = useCallback(() => {
    takeSnapshot();
  }, [takeSnapshot]);

  /**
   * Handle nodes deletion with auto-reconnection
   */
  const onNodesDelete = useCallback(
    (deletedNodes) => {
      const newEdges = deletedNodes.reduce((acc, node) => {
        const incomers = getIncomers(node, nodes, edges);
        const outgoers = getOutgoers(node, nodes, edges);
        const connectedEdges = getConnectedEdges([node], edges);
        
        const remainingEdges = acc.filter(
          (edge) => !connectedEdges.includes(edge)
        );
        
        const createdEdges = incomers.flatMap(({ id: source }) =>
          outgoers.map(({ id: target }) => ({
            id: `${source}->${target}`,
            source,
            target,
            type: 'floating',
          }))
        );

        return [...remainingEdges, ...createdEdges];
      }, edges);

      setEdges(newEdges);
      takeSnapshot();
    },
    [nodes, edges, setEdges, takeSnapshot]
  );

  /**
   * Save mindmap
   */
  const handleSave = useCallback(async () => {
    if (!globalState?.currentCompany) {
      toast({
        title: 'Error',
        description: 'No company selected',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      const mindmapData = {
        nodes,
        edges,
        type: 'mindmap',
      };

      await addDocumentFirebase(
        'flowcharts',
        mindmapData,
        globalState.currentCompany
      );

      toast({
        title: 'Saved',
        description: 'Mindmap saved successfully',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  }, [nodes, edges, globalState?.currentCompany, toast]);

  /**
   * Handle undo
   */
  const handleUndo = useCallback(() => {
    undo();
  }, [undo]);

  /**
   * Handle redo
   */
  const handleRedo = useCallback(() => {
    redo();
  }, [redo]);

  // ==================== RENDER ====================
  
  return (
    <Stack
      w="full"
      h="100vh"
      border="1px"
      borderColor="gray.400"
      borderRadius="lg"
      onKeyDown={onKeyDown}
      tabIndex={0}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodesDelete={onNodesDelete}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onNodeDragStart={onNodeDragStart}
        onSelectionDragStart={onSelectionDragStart}
        fitViewOptions={FIT_VIEW_OPTIONS}
        proOptions={PRO_OPTIONS}
        deleteKeyCode={['Delete', 'Backspace']}
      >
        <Controls />
        <MiniMap />
        
        {/* Main Toolbar */}
        <Panel position="top-right">
          <HStack bg="white" p={2} borderRadius="md" shadow="md" spacing={2}>
            <Tooltip label="Undo (Ctrl+Z)">
              <IconButton
                icon={<FcUndo />}
                onClick={handleUndo}
                isDisabled={!canUndo}
                size="sm"
                aria-label="Undo"
              />
            </Tooltip>
            
            <Tooltip label="Redo (Ctrl+Y)">
              <IconButton
                icon={<FcRedo />}
                onClick={handleRedo}
                isDisabled={!canRedo}
                size="sm"
                aria-label="Redo"
              />
            </Tooltip>
            
            <Tooltip label="Add Node">
              <IconButton
                icon={<FiPlus />}
                onClick={quickAddNode}
                colorScheme="green"
                size="sm"
                aria-label="Add Node"
              />
            </Tooltip>
            
            <Tooltip label="Auto Layout">
              <IconButton
                icon={<FiAlignCenter />}
                onClick={autoLayout}
                colorScheme="purple"
                size="sm"
                aria-label="Auto Layout"
              />
            </Tooltip>
            
            <Tooltip label="Save">
              <IconButton
                icon={<FiSave />}
                onClick={handleSave}
                colorScheme="blue"
                size="sm"
                aria-label="Save"
              />
            </Tooltip>
          </HStack>
        </Panel>

        {/* Keyboard Shortcuts Help */}
        <Panel position="bottom-left">
          <Stack bg="white" p={3} borderRadius="md" shadow="sm" fontSize="xs" spacing={1}>
            <text fontWeight="bold">⌨️ Keyboard Shortcuts:</text>
            <text>• <strong>Enter</strong> - Add child node</text>
            <text>• <strong>Tab</strong> - Add sibling node</text>
            <text>• <strong>Delete</strong> - Delete selected</text>
            <text>• <strong>Ctrl+Z</strong> - Undo</text>
            <text>• <strong>Ctrl+Y</strong> - Redo</text>
            {selectedNode && (
              <text color="blue.500">
                ✓ Node selected - Press Enter/Tab
              </text>
            )}
          </Stack>
        </Panel>
      </ReactFlow>
    </Stack>
  );
}

// ==================== WRAPPER ====================
function MindMapPage() {
  return (
    <ReactFlowProvider>
      <Mindmap />
    </ReactFlowProvider>
  );
}

export default MindMapPage;